import styled from 'styled-components';

import { Text } from '../Text';
import { LOCK_VEIL } from '../../theme/tokens';

/** Which corner the padlock occupies, so it lands where the host card's chevron sits:
 *  `top-right` (Home channel cards) or `bottom-right` (Acquisition Lumio cards). */
export type LockCorner = 'top-right' | 'bottom-right';

/**
 * Frosted "lock veil" that covers a gated card. A translucent neutral-grey scrim over an
 * 8px backdrop blur, so a locked card reads as a calm frosted panel in both themes.
 * Anchored to the host card via `position: absolute; inset: 0`, so the host must be a
 * positioning context (the channel cards already are). It swallows pointer events so the
 * controls beneath a locked card can't be reached.
 *
 * Laid out as a three-row grid — header (top) · caption (middle) · footer (bottom) — and
 * padded to the host card's own padding so the header `[icon] {title}` lands exactly over
 * the enabled card's header, and the padlock lands over its chevron.
 */
export const Veil = styled.div<{ $radius: string; $pad: string }>`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  grid-template-rows: auto 1fr auto;
  padding: ${(p) => p.$pad};

  /* Round with the card it covers (override via the radius prop when the host's corner
     radius lives on a different element than the veil's parent). */
  border-radius: ${(p) => p.$radius};
  /* The veil itself catches the pointer so a locked card's controls stay unreachable. */
  cursor: default;

  background: ${LOCK_VEIL.scrim};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: ${LOCK_VEIL.ink};

  svg {
    display: block;
  }
`;

/** Top row: the header (icon + title) pinned left, and — when `lockCorner` is `top-right`
 *  — the padlock pinned right. Centred vertically like the enabled card's header. */
export const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

/** Bottom row: the padlock pinned right — used when `lockCorner` is `bottom-right` so it
 *  sits where the enabled card's chevron does. */
export const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

/** Header: the channel icon + name, mirroring the enabled card's header. `$gap` matches
 *  the host's icon→name spacing so it overlays its live neighbour exactly. */
export const Header = styled.span<{ $gap: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.$gap}px;
  min-width: 0;
`;

/** Channel icon before the name (inherits the veil's ink via currentColor; its pixel size
 *  is set on the `<Icon size>` prop to match the host card header). */
export const HeaderIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
`;

/** `$fontSize` is set per surface to match the enabled card's heading, so a locked card's
 *  title reads at the same scale as its neighbours. Weight matches the headers (700). */
export const Title = styled(Text)<{ $fontSize: number }>`
  font-size: ${(p) => p.$fontSize}px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${LOCK_VEIL.ink};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Padlock slot — replaces the enabled card's chevron. */
export const Lock = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  color: ${LOCK_VEIL.ink};
`;

/** Middle row: the "Unlocking soon" status caption, centred in the card. Slightly muted
 *  against the header so the title leads and the status reads as secondary. */
export const Center = styled.div`
  display: grid;
  place-items: center;
  min-height: 0;
`;

export const Caption = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${LOCK_VEIL.ink};
  opacity: 0.72;
`;
